import { Controller } from '@hotwired/stimulus';
import { WidgetManager } from '@infakt/widgets/vanilla-sdk';

import { trackError } from '../shared/lib/errorTracking';

const SKELETON_TIMEOUT_MS = 8000;

export default class extends Controller {
  static targets = ['subnavActions'];

  static values = {
    containerId: String,
    erste: Boolean,
    ksefDowntime: Boolean,
    skPricing: Boolean,
    newAjsClients: Boolean,
    newAjsSellers: Boolean,
    newAjsStatistics: Boolean,
    darkMode: Boolean,
    hasDefinedInvoices: Boolean,
  };

  async connect() {
    const connectionToken = {};
    this.connectionToken = connectionToken;

    try {
      const manager = new WidgetManager();
      const widget = await manager.init({
        name: 'header',
        containerId: this.containerIdValue,
        props: {
          flags: {
            erste: this.ersteValue,
            ksefDowntime: this.ksefDowntimeValue,
            skPricing: this.skPricingValue,
            newAjsClients: this.newAjsClientsValue,
            newAjsSellers: this.newAjsSellersValue,
            newAjsStatistics: this.newAjsStatisticsValue,
            darkMode: this.darkModeValue,
          },
          hasDefinedInvoices: this.hasDefinedInvoicesValue,
        },
        onError: (error) => {
          trackError(
            error instanceof Error ? error : new Error(String(error ?? 'Unknown widget error')),
            { context: 'header_widget' },
            { errorMessage: error?.message },
          );
        },
      });

      if (this.connectionToken !== connectionToken) return;

      this.manager = manager;
      this.widget = widget;

      await this.widget.mount();

      if (this.connectionToken !== connectionToken) {
        this.widget.unmount();
        this.widget = undefined;
        return;
      }

      this.removeSkeletonWhenReady();
    } catch (error) {
      console.error('[header-widget]', error);
      this.revealHeaderFallback();
    }
  }

  disconnect() {
    this.connectionToken = null;
    this.widget?.unmount();
    this.widget = undefined;
  }

  removeSkeletonWhenReady() {
    const container = document.getElementById(this.containerIdValue);
    const start = performance.now();

    const tick = () => {
      const rendered = container?.shadowRoot?.querySelector('header, nav');
      const timedOut = performance.now() - start > SKELETON_TIMEOUT_MS;

      if (rendered || timedOut) {
        this.revealHeaderFallback();
        return;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  revealHeaderFallback() {
    this.element.querySelectorAll('[data-infakt-header-skeleton]').forEach((skeleton) => skeleton.remove());
    this.revealSubnavActions();
  }

  revealSubnavActions() {
    if (!this.hasSubnavActionsTarget) return;

    this.subnavActionsTarget.classList.replace('hidden', 'flex');
  }
}
